import axios from "axios";
import { API_BASE_URL } from "../utils/constants";
import { getAuthToken } from "../utils/storage";
import { extractApiError } from "../utils/apiError";
import { applyWarehouseFilter, warehouseParamsSerializer, appendWarehouseParams } from "../utils/warehouseParams";

// Axios instance for purchase orders API
const purchaseOrdersAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { "Content-Type": "application/json" },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
purchaseOrdersAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error),
);

// Redirect on auth failure
purchaseOrdersAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    // Skip logout on 401 for bulk-delete endpoints (which use 401 for password errors)
    if (err.response?.status === 401 && !err.config?.url?.includes('/bulk-delete/')) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_data");
      sessionStorage.removeItem("auth_token");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user_data");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

const CACHE_PREFIX = "erp_purchase_orders_cache_v1";
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(
      cacheKey(key),
      JSON.stringify({ value, expires: Date.now() + ttl }),
    );
  } catch (e) {}
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try {
    memoryCache.delete(key);
  } catch (e) {}
  try {
    sessionStorage.removeItem(cacheKey(key));
  } catch (e) {}
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === "string" && key.startsWith("all::")) {
        const arr = entry?.value;
        if (Array.isArray(arr)) writeMemoryCache(key, modifierFn(arr.slice()));
      }
    }
  } catch (e) {}

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const val = parsed?.value;
          if (Array.isArray(val)) {
            parsed.value = modifierFn(val.slice());
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) {}
      }
    }
  } catch (e) {}
}

const purchaseOrderService = {
  // 1. post: /api/v1/purchase-orders/existing-supplier
  async createPurchaseOrderExistingSupplier(payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        "/purchase-orders/existing-supplier",
        payload,
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try {
        const key = `byId::${created.id}`;
        writeMemoryCache(key, created);
        writeCache(key, created);
      } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 2. post: /api/v1/purchase-orders/new-supplier
  async createPurchaseOrderNewSupplier(payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        "/purchase-orders/new-supplier",
        payload,
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try {
        const key = `byId::${created.id}`;
        writeMemoryCache(key, created);
        writeCache(key, created);
      } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createPurchaseOrderExistingSupplierWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await purchaseOrdersAPI.post(
        '/purchase-orders/existing-supplier/with-attachments',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createPurchaseOrderNewSupplierWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await purchaseOrdersAPI.post(
        '/purchase-orders/new-supplier/with-attachments',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 3. get: /api/v1/purchase-orders
  async getAllPurchaseOrders(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) {
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }
    }

    try {
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      else queryParams.append('skip', 0);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      else queryParams.append('limit', 20);
      if (params.search) queryParams.append('search', params.search);
      if (params.from_date) queryParams.append('from_date', params.from_date);
      if (params.to_date) queryParams.append('to_date', params.to_date);
      if (params.status) {
        const statuses = Array.isArray(params.status) ? params.status : [params.status];
        statuses.forEach(s => queryParams.append('status', s));
      }
      appendWarehouseParams(queryParams);
      const response = await purchaseOrdersAPI.get(`/purchase-orders?${queryParams.toString()}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 4. get: /api/v1/purchase-orders/{po_id}
  async getPurchaseOrderById(poId, options = {}) {
    const { force = false } = options;
    const key = `byId::${poId}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) {
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }

      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find(
          (i) => String(i.id || i.po_id) === String(poId),
        );
        if (found) {
          writeMemoryCache(key, found);
          writeCache(key, found);
          return { success: true, data: found };
        }
      }
    }

    try {
      const response = await purchaseOrdersAPI.get(`/purchase-orders/${poId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 5. put: /api/v1/purchase-orders/{po_id}
  async updatePurchaseOrder(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-orders/${poId}`,
        payload,
      );
      const updated = response.data;
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async deletePurchaseOrder(poId) {
    try {
      const response = await purchaseOrdersAPI.delete(
        `/purchase-orders/${poId}`,
      );
      updateFullListCachesWithModifier((arr) =>
        arr.filter((i) => String(i.id) !== String(poId)),
      );
      try {
        deleteCacheEntry(`byId::${poId}`);
      } catch (e) {}
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async deleteAllPurchaseOrders(payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-orders/delete-all`, 
        payload
      );
      try {
        this.clearCache();
      } catch (e) {}
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async bulkDeletePurchaseOrders(purchaseOrderIds = []) {
    try {
      const ids = (purchaseOrderIds || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
      const response = await purchaseOrdersAPI.post(`/purchase-orders/bulk-delete`, ids);
      updateFullListCachesWithModifier((arr) =>
        arr.filter((o) => !ids.includes(o.id || o.po_id))
      );
      ids.forEach(id => {
        deleteCacheEntry(`byId::${id}`);
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 7. post: /api/v1/purchase-orders/{po_id}/confirm
  async confirmPurchaseOrder(poId) {
    try {
      // Call the confirm endpoint
      await purchaseOrdersAPI.post(`/purchase-orders/${poId}/confirm`);

      // Refetch the updated purchase order since the API doesn't return it
      const fetchResponse = await purchaseOrdersAPI.get(
        `/purchase-orders/${poId}`,
      );
      const updated = fetchResponse.data;

      // Update caches with the fresh data
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 8. post: /api/v1/purchase-orders/{po_id}/cancel
  async cancelPurchaseOrder(poId) {
    try {
      // Call the cancel endpoint
      await purchaseOrdersAPI.post(`/purchase-orders/${poId}/cancel`);

      // Refetch the updated purchase order since the API doesn't return it
      const fetchResponse = await purchaseOrdersAPI.get(
        `/purchase-orders/${poId}`,
      );
      const updated = fetchResponse.data;

      // Update caches with the fresh data
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 9. get: /api/v1/purchase-advices
  async getAllPurchaseAdvices(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get("/purchase-advices", {
        params,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 10. get: /api/v1/purchase-advices/{po_id}
  async getPurchaseAdviceById(poId) {
    try {
      const response = await purchaseOrdersAPI.get(`/purchase-advices/${poId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // post: /api/v1/purchase-advices/{advice_id}/save-unloading-draft
  async saveUnloadingDraft(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-advices/${adviceId}/save-unloading-draft`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 11. put: /api/v1/purchase-advices/{po_id}
  async updatePurchaseAdvice(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-advices/${poId}`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 11b. delete: /api/v1/purchase-advices/{po_id}
  async deletePurchaseAdvice(poId) {
    try {
      const response = await purchaseOrdersAPI.delete(
        `/purchase-advices/${poId}`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 12. post: /api/v1/purchase-advices/{po_id}/generate
  async generatePurchaseAdvice(poId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-orders/${poId}/advices`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 12b. post: /api/v1/purchase-advices/{advice_id}/assign-supervisor
  async assignSupervisorToAdvice(adviceId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/assign-supervisor`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 13. post: /api/v1/purchase-advices/{po_id}/start-loading
  async startLoadingPurchaseAdvice(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${poId}/start-loading`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 14. post: /api/v1/purchase-advices/{po_id}/complete-loading
  async completeLoadingPurchaseAdvice(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${poId}/complete-loading`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 14b. post: /api/v1/purchase-advices/{advice_id}/start-unloading
  async startUnloadingPurchaseAdvice(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/start-unloading`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 14c. post: /api/v1/purchase-advices/{advice_id}/complete-unloading
  async completeUnloadingPurchaseAdvice(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/complete-unloading`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 15. get: /api/v1/purchase-challans
  async getAllPurchaseChallans(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get("/purchase-challans", {
        params,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 16. get: /api/v1/purchase-challans/{po_id}
  async getPurchaseChallanById(poId) {
    try {
      const response = await purchaseOrdersAPI.get(
        `/purchase-challans/${poId}`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 17. put: /api/v1/purchase-challans/{po_id}
  async updatePurchaseChallan(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-challans/${poId}`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // PUT /purchase-advices/{advice_id}/edit-challan
  async editPurchaseChallan(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-advices/${adviceId}/edit-challan`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 18. post: /api/v1/purchase-orders/{po_id}/generate-challan
  async generatePurchaseChallan(poId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-orders/${poId}/generate-challan`,
        payload,
      );
      const updated = response.data;
      // Update cache - PO is now COMPLETED with challan details
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 18b. post: /api/v1/purchase-advices/{advice_id}/generate-challan
  async generateAdviceChallan(adviceId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/generate-challan`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // post: /api/v1/purchase-advices/{advice_id}/mark-challan-printed
  async markAdviceChallanPrinted(adviceId) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/mark-challan-printed`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 18c. post: /api/v1/purchase-advices/{advice_id}/mark-received
  async markAdviceReceived(adviceId) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/mark-received`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 19b. post: /api/v1/purchase-orders/{po_id}/mark-all-advised
  async markAllAdvised(poId) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-orders/${poId}/mark-all-advised`,
      );
      const updated = response.data;
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? { ...i, ...updated } : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 20. post: /api/v1/purchase-orders/{po_id}/complete
  async completePurchaseOrder(poId) {
    try {
      // Call the complete endpoint
      await purchaseOrdersAPI.post(`/purchase-orders/${poId}/complete`);

      // Refetch the updated purchase order since the API doesn't return it
      const fetchResponse = await purchaseOrdersAPI.get(
        `/purchase-orders/${poId}`,
      );
      const updated = fetchResponse.data;

      // Update caches with the fresh data
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 21. get: /api/v1/purchase-orders/{po_id}/complete
  async getCompletedPurchaseOrder(poId, params = {}) {
    try {
      const response = await purchaseOrdersAPI.get(
        `/purchase-orders/${poId}/complete`,
        { params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 22. delete: /api/v1/purchase-daybook/{po_id}
  async deletePurchaseDaybookEntry(poId) {
    try {
      const response = await purchaseOrdersAPI.delete(
        `/purchase-daybook/${poId}`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 23. get: /api/v1/reports/pending-purchase-advice
  async getPendingPurchaseAdviceReport(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get(
        "/reports/pending-purchase-advice",
        { params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 24. get: /api/v1/reports/purchase-estimate
  async getPurchaseEstimateReport(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get(
        "/reports/purchase-estimate",
        { params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 25. get: /api/v1/purchase-daybook
  async getPurchaseDaybook(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get("/purchase-daybook", {
        params,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async exportPurchaseChallans(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get('/purchase-challans/export/download', {
        params,
        responseType: 'blob',
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async exportPurchaseDaybook(params = {}) {
    try {
      const query = new URLSearchParams({
        ...params,
        format: params.format || 'csv'
      });
      appendWarehouseParams(query);
      const response = await purchaseOrdersAPI.get('/purchase-challans/export/download', {
        params: Object.fromEntries(query.entries()),
        responseType: 'blob',
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  clearCache() {
    try {
      for (const [key] of memoryCache.entries()) {
        if (typeof key === "string" && key.includes("purchase_orders"))
          memoryCache.delete(key);
      }
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) {}
  },

  async sendNotification(id, type = 'email') {
    try {
      const response = await purchaseOrdersAPI.post(`/purchase-orders/${id}/send-notification`, null, { params: { type } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },
};

export default purchaseOrderService;
